import { __awaiter, __extends, __generator } from "tslib";
import { AuthType, } from '@office-start/auth-types';
import { resolveConfig } from '@office-start/config-resolver-service';
import { logFeature } from '@office-start/telemetry-service';
import { BasePwaAccessTokenService } from './BasePwaAccessTokenService';
var PwaAccessTokenService = /** @class */ (function (_super) {
    __extends(PwaAccessTokenService, _super);
    function PwaAccessTokenService(clientStartupConfig, context, pwaConfig, officeApiConfig) {
        var _this = this;
        var tokenFormat = clientStartupConfig.authType === AuthType.MSA
            ? 'RPSCompact'
            : 'JWT';
        var tokenSource = 'WAM';
        _this = _super.call(this, tokenFormat, tokenSource) || this;
        _this.clientStartupConfig = clientStartupConfig;
        _this.context = context;
        _this.pwaConfig = pwaConfig;
        _this.officeApiConfig = officeApiConfig;
        _this.init(tokenFormat, tokenSource);
        _this.wamAccessTokenRequests =
            window.wamAccessTokenRequests ||
                new Map();
        _this.resourceMap = _this.pwaConfig.resourceUrlMap
            ? new Map(Object.entries(_this.pwaConfig.resourceUrlMap))
            : new Map();
        // Override the shellTokenRequestDictionary functions initialized in office-pwa.ts because this class checks for token validity and
        // caches them (in wamAccessTokenRequests promises)
        var shellTokenRequestDictionary = window.shellTokenRequestDictionary;
        if (shellTokenRequestDictionary && _this.resourceMap.size > 0) {
            ['Graph', 'WCSS'].forEach(function (resource) {
                var _a;
                shellTokenRequestDictionary[(_a = _this.resourceMap.get(resource)) !== null && _a !== void 0 ? _a : ''] = function () {
                    return _this.getAccessTokenResponse(resource).then(function (response) { return response.token; });
                };
            });
        }
        return _this;
    }
    PwaAccessTokenService.getInstance = function () {
        return __awaiter(this, void 0, void 0, function () {
            var startupConfig, sharedContextConfig, pwaConfig, officeHomeAPIConfig, authTypeString;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!!PwaAccessTokenService.instance) return [3 /*break*/, 5];
                        return [4 /*yield*/, resolveConfig('clientStartupConfiguration')];
                    case 1:
                        startupConfig = _a.sent();
                        if (!startupConfig) {
                            throw new Error('Failed to resolve clientStartupConfiguration');
                        }
                        return [4 /*yield*/, resolveConfig('sharedContext')];
                    case 2:
                        sharedContextConfig = _a.sent();
                        if (!sharedContextConfig) {
                            throw new Error('Failed to resolve sharedContext');
                        }
                        return [4 /*yield*/, resolveConfig('pwaConfig')];
                    case 3:
                        pwaConfig = _a.sent();
                        if (!pwaConfig) {
                            throw new Error('Failed to resolve pwaConfig');
                        }
                        return [4 /*yield*/, resolveConfig('officeHomeApiConfig')];
                    case 4:
                        officeHomeAPIConfig = _a.sent();
                        if (!officeHomeAPIConfig) {
                            throw new Error('Failed to resolve officeHomeApiConfig');
                        }
                        authTypeString = startupConfig.authTypeString;
                        if (authTypeString === 'MSA') {
                            startupConfig.authType = AuthType.MSA;
                            // In PWA MSA, add puid and UPN retrieved from WAM to config.
                        }
                        else if (authTypeString === 'OrgId') {
                            startupConfig.authType = AuthType.OrgId;
                        }
                        else {
                            startupConfig.authType = AuthType.Unknown;
                        }
                        PwaAccessTokenService.instance = new PwaAccessTokenService(startupConfig, sharedContextConfig, pwaConfig, officeHomeAPIConfig);
                        _a.label = 5;
                    case 5: return [2 /*return*/, PwaAccessTokenService.instance];
                }
            });
        });
    };
    PwaAccessTokenService.prototype.hasPermanentAuthError = function () {
        throw new Error('Method not implemented.');
    };
    PwaAccessTokenService.prototype.hasOneDriveToken = function () {
        // NOOP: Assume Tokens are fetched correctly by WAM
        // TODO: Figure out how to handle slow tokens/tokens that need to be refreshed
        return true;
    };
    PwaAccessTokenService.prototype.hasCAPError = function (_tokenConsumer, _resourceUrl) {
        // Not sure if it's possible to check for CAP error from WAM.
        return false;
    };
    // TODO: Observe forceFetch param
    PwaAccessTokenService.prototype.getAccessTokenResponse = function (tokenConsumer, forceFetch, resourceUrl, _allowInteractive, _capDialogData, clientId, _scope, _claims, claimsChallenge) {
        return this.getAccessTokenPromise(tokenConsumer, resourceUrl, forceFetch, clientId, claimsChallenge);
    };
    PwaAccessTokenService.prototype.getAccessTokenPromise = function (tokenConsumer, resourceUrl, forceFetch, clientId, claimsChallenge) {
        var _this = this;
        var _a;
        var startupConfig = this.clientStartupConfig;
        var tokenFormat;
        if (tokenConsumer === 'OfficeHome' &&
            startupConfig.sharedClientStartupContext.pwaV2TokenEnabled &&
            this.officeApiConfig.jwtAuthEnabled) {
            // V2 OfficeHome token is JWT for MSA and AAD
            tokenFormat = 'JWT';
        }
        else {
            tokenFormat =
                startupConfig.authType === AuthType.MSA ? 'RPSCompact' : 'JWT';
        }
        if (this.context.inMockMode) {
            return Promise.resolve({
                success: true,
                token: 'mock token',
                format: tokenFormat,
                source: 'WAM',
            });
        }
        var resource;
        var resultPromise;
        var resourceFriendlyName = tokenConsumer;
        if (!resourceUrl) {
            resource = (_a = this.resourceMap.get(resourceFriendlyName)) !== null && _a !== void 0 ? _a : '';
        }
        else {
            resource = resourceUrl;
        }
        if (!resource) {
            return Promise.reject('Invalid resource');
        }
        if (this.wamAccessTokenRequests.has(resource)) {
            var accessTokenRequest = this.wamAccessTokenRequests.get(resource);
            if (!accessTokenRequest) {
                return Promise.reject('Invalid resource');
            }
            var accessTokenPromise = accessTokenRequest.result;
            resultPromise = accessTokenPromise.then(function (tokenResponse) {
                var currentRequest = _this.wamAccessTokenRequests.get(resource);
                if (currentRequest === null || currentRequest === void 0 ? void 0 : currentRequest.pending) {
                    currentRequest.pending = false;
                    _this.logAccessTokenState('Succeeded', resourceFriendlyName);
                    return tokenResponse;
                }
                if (!_this.tokenIsValid(tokenResponse) || forceFetch) {
                    var refreshAccessToken = window.getWamAccessToken(resource, resourceFriendlyName, clientId, claimsChallenge, forceFetch);
                    _this.logAccessTokenState('RefreshTokenRequested', resourceFriendlyName);
                    _this.wamAccessTokenRequests.set(resource, {
                        pending: true,
                        result: refreshAccessToken,
                    });
                    return refreshAccessToken;
                }
                _this.logAccessTokenState('Fetched', resourceFriendlyName);
                return tokenResponse;
            });
        }
        else {
            resultPromise = window.getWamAccessToken(resource, resourceFriendlyName, clientId, claimsChallenge, forceFetch);
            this.wamAccessTokenRequests.set(resource, {
                pending: true,
                result: resultPromise,
            });
        }
        return resultPromise.catch(function () {
            // If token fetch failed we should retry token fetch by removing resource from wamAccessTokenRequests
            // so subsequent token fetch can retry calling getWamAccessToken
            if (_this.wamAccessTokenRequests.has(resource)) {
                _this.wamAccessTokenRequests.delete(resource);
            }
            // getWamAccessToken should have telemetry already so we don't include more information in the error here
            return {
                success: false,
                token: '',
                format: tokenFormat,
                source: 'WAM',
                error: new Error('Token fetch failed from WAM'),
            };
        });
    };
    PwaAccessTokenService.prototype.tokenIsValid = function (tokenResponse) {
        return (tokenResponse &&
            (tokenResponse.format !== 'JWT' ||
                !tokenResponse.expiryMs ||
                tokenResponse.expiryMs > new Date().getTime()));
    };
    PwaAccessTokenService.prototype.logAccessTokenState = function (state, resourceFriendlyName) {
        logFeature({
            featureName: "Hwa" /* KnownFeatureNames.Hwa */,
            eventName: "WAM_Token" /* KnownRequestInstrumentationEvents.WamTokenRequest */,
            params: {
                ResourceUrl: resourceFriendlyName,
                State: state,
            },
        });
    };
    return PwaAccessTokenService;
}(BasePwaAccessTokenService));
export { PwaAccessTokenService };
//# sourceMappingURL=PwaAccessTokenService.js.map